import ReactDOM from "react-dom";
import { FC, useEffect, useMemo, useState, CSSProperties, Key, useRef, ChangeEvent, MouseEvent, RefObject, KeyboardEvent, } from "react";
// 组件
import { useLayoutContext } from '../../../PageLayout/context/LayoutContext';
import { ProTabs, ProTag, } from '../../../antd';
import { CheckOutlined, CloseOutlined, SearchOutlined, } from "@ant-design/icons";
// 方法
import classNames from "classnames";
import { renderEnumName } from '../../../utils/renderUtils';
import { markSearchValue } from "../../../SuperSelect/util";
import { filterSearchOptions, flatSelectTabOptions, formatOptions, getSelectTabOptions, isOptionSelect, setOptionStyle, sortOptions, } from "../../util";
// 常量
import { SelectTabDropdownBaseProps, SelectTabInOption, DEFAULT_FIELD_NAMES, } from "../../interface";

interface SelectTabDropdownProps extends SelectTabDropdownBaseProps {
  /** 是否显示 */
  show?: boolean;
  /** 距离页面左侧的距离 */
  left?: number;
  /** 距离页面头部的距离 */
  top?: number;
  /** 宽度 */
  width?: number;
  /** 距离页面底部的距离 */
  bottom?: number;
  /** dropdownRef */
  dropdownRef: RefObject<HTMLDivElement>;
  /** 点击选项 */
  onClickOption: (optionValue?: Key) => void;
  /** 点击全选 */
  onClickSelectAll: (groupOptionValues?: Key[]) => void;
  /** 点击反选 */
  onReverseSelect: (groupOptionValues?: Key[]) => void;
  /** 搜索回调 */
  onSearch: (searchValue?: string) => void;
}

const SelectTabDropdown: FC<SelectTabDropdownProps> = props => {
  const {
    options: selectOption, columnNumber = 2, value: selectValue, fieldNames, show, left, top, bottom, width,
    dropdownRef, showSearch, tabItems, dropdownStyle, dropdownClassName, isLocalSearch, searchPlaceholder, listHeight,
    isMultiple, allowClear, optionsEmptyTip, searchOptionsEmptyTip, searchDiscriminateCase, isSelectAll, isReverseSelection,
    isGroupSelectAll, isGroupReverseSelection, showTab, zIndex,
    getPopupContainer, onClickOption, onSearch, onClear, onClickSelectAll, onReverseSelect, sort, tabsChangeFormatOption,
  } = props;
  const [options, setOptions] = useState<SelectTabInOption[]>(); // 分组后的选项
  const [tabKey, setTabKey] = useState<string>(); // 选中tab
  const [active, setActive] = useState<Key>(); // 预选中节点
  const [searchValue, setSearchValue] = useState<string>(); // 搜索内容
  const inputRef = useRef<HTMLInputElement>(null); // 搜索选项输入框
  const scrollRef = useRef<HTMLDivElement>(null); // 选项滚动区域
  const timer = useRef<any>(); // 延时过滤选项定时器
  const flatOptions = useRef<SelectTabInOption[]>(); // 拉平选项
  const dropdownTagsRef = useRef<HTMLDivElement>(null); // 已选数据tags节点
  const { enumData } = useLayoutContext();
  useEffect(() => {
    if (dropdownTagsRef.current) {
      const dropdownTagsHeight = dropdownTagsRef.current.scrollHeight;
      if (!selectValue) {
        dropdownTagsRef.current.style.height = '';
        dropdownTagsRef.current.style.flex = '';
        dropdownTagsRef.current.style.overflow = '';
        return;
      }
      if (dropdownTagsHeight > 86) {
        dropdownTagsRef.current.style.height = '87px';
        dropdownTagsRef.current.style.flex = '0 0 87px';
        dropdownTagsRef.current.style.overflow = 'auto';
        return;
      }
      dropdownTagsRef.current.style.height = '';
      dropdownTagsRef.current.style.flex = '';
      dropdownTagsRef.current.style.overflow = '';
      // if (dropdownTagsHeight === 80 || !selectValue) {
      //   dropdownTagsRef.current.style.height = '';
      //   dropdownTagsRef.current.style.flex = '';
      //   dropdownTagsRef.current.style.overflow = '';
      //   return;
      // }
      // dropdownTagsRef.current.style.height = `${dropdownTagsHeight}px`;
      // dropdownTagsRef.current.style.flex = `0 0 ${dropdownTagsHeight}px`;
      // dropdownTagsRef.current.style.overflow = 'hidden';
    }
  }, [ selectValue ]);
  useEffect(() => {
    if (show) {
      inputRef.current?.focus();
      if (selectValue) {
        if (Array.isArray(selectValue)) {
          setActive(selectValue[0]);
        } else {
          setActive(selectValue);
        }
      } else {
        setActive(flatOptions.current?.[0]?.value || selectOption?.[0]?.value);
      }
    } else {
      setActive(undefined);
    }
  }, [show]);
  useEffect(() => {
    const findItem = tabItems?.find(item => item.value === tabKey) || tabItems?.[0];
    const newSelectOption = tabsChangeFormatOption ? tabsChangeFormatOption(selectOption, tabKey) : selectOption;
    const flterOptions = isLocalSearch ? filterSearchOptions(newSelectOption, searchValue, fieldNames) : newSelectOption;
    const newOptions = formatOptions(flterOptions, findItem, fieldNames);
    flatOptions.current = flatSelectTabOptions(newOptions);
    if (sort) {
      const newSortOptions = sortOptions(newOptions, sort);
      setOptions(newSortOptions);
      return;
    }
    setOptions(newOptions);
  }, [selectOption, tabKey,]);
  useEffect(() => {
    timer.current && clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const findItem = tabItems?.find(item => item.value === tabKey) || tabItems?.[0];
      const newSelectOption = tabsChangeFormatOption ? tabsChangeFormatOption(selectOption, tabKey) : selectOption;
      const flterOptions = isLocalSearch ? filterSearchOptions(newSelectOption, searchValue, fieldNames) : newSelectOption;
      const newOptions = formatOptions(flterOptions, findItem, fieldNames);
      flatOptions.current = flatSelectTabOptions(newOptions);
      setActive(flterOptions?.[0]?.value);
      if (sort) {
        const newSortOptions = sortOptions(newOptions, sort);
        setOptions(newSortOptions);
        return;
      }
      setOptions(newOptions);
    }, 300);
  }, [selectOption, searchValue]);
  /** 键盘事件 */
  useEffect(() => {
    const handleKeydown = (event: any) => {
      if (!options) return;
      const keyNum = event.key;
      if (keyNum === 'ArrowDown' || keyNum === 'ArrowUp') {
        const currentOptions = flatOptions.current;
        if (!currentOptions) return;
        if (currentOptions.length > columnNumber) {
          const findIndex = currentOptions.findIndex(item => item.value === active) || 0;
          let nextIndex = keyNum === 'ArrowDown' ? findIndex + columnNumber : findIndex - columnNumber;
          if (nextIndex >= currentOptions.length) {
            nextIndex = nextIndex - currentOptions.length;
          }
          if (nextIndex < 0) {
            nextIndex = currentOptions.length + nextIndex;
          }
          const nextOption = currentOptions[nextIndex];
          nextOption && setActive(nextOption.value);
        }
        return;
      }
      if (keyNum === 'ArrowLeft' || keyNum === 'ArrowRight') {
        const currentOptions = flatOptions.current;
        if (!currentOptions || options.length < 1) return;
        const findIndex = currentOptions.findIndex(item => item.value === active) || 0;
        let nextIndex = keyNum === 'ArrowRight' ? findIndex + 1 : findIndex - 1;
        if (nextIndex >= currentOptions.length) nextIndex = 0;
        if (nextIndex < 0) nextIndex = currentOptions.length - 1;
        const nextOption = currentOptions[nextIndex];
        nextOption && setActive(nextOption.value);
        return;
      }
      if (keyNum === 'Enter' && active) {
        onClickOption(active);
      }
    };
    if (show) {
      window.addEventListener('keydown', handleKeydown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  }, [show, options, active, selectValue, columnNumber]);
  /** tabs切换 */
  const handleChangeTab = (value: string) => {
    if (value === tabKey) return;
    if (scrollRef.current) {
      const scrollTop = scrollRef.current.scrollTop;
      if (scrollTop) {
        scrollRef.current.scrollTop = 0;
      }
    }
    setTabKey(value);
  };
  /** 点击选项 */
  const handleClickOption = (value?: string) => {
    onClickOption?.(value);
  };
  /** 鼠标移入选项 */
  const handleItemMouseEnter = (optionValue?: string) => {
    setActive(optionValue);
  };
  /** 搜索内容改变 */
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const filterValue = event.target.value.trim();
    setSearchValue(filterValue);
    !isLocalSearch && onSearch(filterValue);
  };
  /** 清除搜索内容 */
  const handleClearSearch = (event: MouseEvent<HTMLSpanElement>) => {
    event.stopPropagation();
    setSearchValue(undefined);
    if (inputRef.current) inputRef.current.value = '';
    !isLocalSearch && onSearch();
  };
  /** input搜索框键盘事件 */
  const handleInputKeydown = (event: KeyboardEvent<HTMLInputElement>) => {
    const keyNum = event.key;
    if (keyNum === 'ArrowDown' || keyNum === 'ArrowUp' || keyNum === 'ArrowLeft' || keyNum === 'ArrowRight') {
      event.preventDefault();
    }
  }
  /** 清空选项 */
  const handleClear = (event: MouseEvent<HTMLSpanElement>) => {
    event.stopPropagation();
    onClear?.();
  };
  /** 全选 */
  const handleSelectAll = (groupOptionValues?: Key[]) => {
    onClickSelectAll(groupOptionValues);
  };
  /** 反选 */
  const handleReverseSelect = (groupOptionValues?: Key[]) => {
    onReverseSelect(groupOptionValues);
  };
  /** 已选项 */
  const selectedTags = useMemo(() => {
    if (!isMultiple) return;
    const selectOptions = getSelectTabOptions(selectValue, selectOption, fieldNames);
    return selectOptions;
  }, [selectValue, selectOption, fieldNames, isMultiple]);
  /** dropdown样式 */
  const innerDropdownStyle: CSSProperties = useMemo(() => {
    const newDropdownStyle: CSSProperties = {
      width,
      transformOrigin: 'center top',
      maxHeight: listHeight,
      zIndex: zIndex || 10001,
      ...dropdownStyle,
    };
    if (!show) {
      newDropdownStyle.display = 'none';
    } else {
      if (left) newDropdownStyle.left = left;
      if (top) newDropdownStyle.top = (top || 0) + 4;
      if (bottom) newDropdownStyle.bottom = bottom;
    }
    return newDropdownStyle;
  }, [show, left, top, bottom, listHeight, zIndex, ]);
  /** dropdown类名 */
  const dropdownClasses = useMemo(() => {
    return classNames('yb-select-tab-dropdown', dropdownClassName);
  }, [dropdownClassName]);
  /** 选项容器类名 */
  const optionsClasses = useMemo(() => {
    const valueName = fieldNames?.value || DEFAULT_FIELD_NAMES.value;
    const currentTabKey = tabKey || tabItems?.[0].value;
    return classNames('dropdown-options', {
      'dropdown-options-horizontal': currentTabKey === valueName,
      'dropdown-options-vertical': currentTabKey !== valueName,
    });
  }, [tabKey]);
  const containerDom: HTMLElement = useMemo(() => {
    if (!getPopupContainer) return document.body;
    if (typeof getPopupContainer === 'function') return getPopupContainer();
    return getPopupContainer;
  }, [getPopupContainer]);
  const renderHTML =  (
    <div ref={dropdownRef} className={dropdownClasses} style={innerDropdownStyle}>
      {
        showTab && (
          <ProTabs
            onChange={handleChangeTab}
            activeKey={tabKey}
            className="dropdown-tabs"
            items={tabItems?.map(item => {
              const { label, value, icon, } = item;
              return {
                label,
                key: value,
              };
            })}
          />
        )
      }
      {
        showSearch && (
          <div className="dropdown-search">
            <SearchOutlined className="dropdown-search-icon" />
            <input
              className="dropdown-search-input"
              ref={inputRef}
              placeholder={searchPlaceholder}
              type='text'
              autoComplete="off"
              onChange={handleChange}
              onKeyDown={handleInputKeydown}
            />
            {
              allowClear && !!searchValue && (
                <CloseOutlined
                  className="dropdown-search-close"
                  onClick={handleClearSearch}
                />
              )
            }
          </div>
        )
      }
      {
        isMultiple && (
          <div className="dropdown-tags" ref={dropdownTagsRef}>
            {
              isMultiple && (
                <>
                  {
                    isSelectAll && (
                      <span
                        className="dropdown-tags-select-all"
                        onClick={() => handleSelectAll(flatOptions.current?.map(v => v.value as string))}
                      >全选</span>
                    )
                  }
                  {
                    isReverseSelection && (
                      <span
                        className="dropdown-tags-select-all"
                        onClick={() => handleReverseSelect(flatOptions.current?.map(v => v.value as string))}
                      >反选</span>
                    )
                  }
                </>
              )
            }
            <span className="dropdown-tags-title">已选项:</span>
            {
              selectedTags && selectedTags.length ? (
                <>
                  <span className="dropdown-tags-clear" onClick={handleClear}>清空</span>
                  {
                    selectedTags.map(item => {
                      const label = item[fieldNames?.label || DEFAULT_FIELD_NAMES.label];
                      const value = item[fieldNames?.value || DEFAULT_FIELD_NAMES.value];
                      return (
                        <ProTag
                          key={value}
                          closable
                          onClose={() => handleClickOption(value)}
                        >
                          {label}
                        </ProTag>
                      );
                    })
                  }
                </>
              ) : (
                <span className="dropdown-tags-empty">无</span>
              )
            }
          </div>
        )
      }
      <div className={optionsClasses} ref={scrollRef}>
        {
          options?.length ? (
            <>
              {
                options.map((option, optionIndex) => {
                  const { label, subLabel, value, icon, children, } = option;
                  const isSelect = isOptionSelect(selectValue, value);
                  const itemClasses = classNames('dropdown-options-item', {
                    'option-active': value === active,
                    'option-select': isSelect,
                  });
                  const formatLabel = markSearchValue(label, searchValue, searchDiscriminateCase);
                  const formatSubLabel = markSearchValue(subLabel, searchValue, searchDiscriminateCase);
                  if (Array.isArray(children) && children.length) {
                    const findItem = tabItems?.find(item => item.value === tabKey) || tabItems?.[0];
                    let groupLabel = label;
                    if (findItem && findItem.enumCode && enumData && enumData[findItem.enumCode]) {
                      groupLabel = renderEnumName(label as string, enumData[findItem.enumCode].items);
                    }
                    return (
                      <div key={value} className="dropdown-options-group">
                        <div key={value} className="dropdown-options-group-header">
                          {
                            isMultiple && (
                              <>
                                {
                                  isGroupSelectAll && (
                                    <span
                                      className="dropdown-tags-select-all"
                                      onClick={() => handleSelectAll(children.map(v => v.value as string))}
                                    >全选</span>
                                  )
                                }
                                {
                                  isGroupReverseSelection && (
                                    <span
                                      className="dropdown-tags-select-all"
                                      onClick={() => handleReverseSelect(children.map(v => v.value as string))}
                                    >反选</span>
                                  )
                                }
                              </>
                            )
                          }
                          {
                            icon && (
                              <span className='dropdown-options-item-icon'>{icon}</span>
                            )
                          }
                          <span className='dropdown-options-item-label'>{groupLabel}</span>
                          <span className='dropdown-options-item-sub-label'>{subLabel}</span>
                        </div>
                        <div className="dropdown-options-group-item">
                          {
                            children.map((item, itemIndex) => {
                              const { label: childrenLabel, subLabel: childrenSubLabel, value: childrenValue, icon: childrenIcon, } = item;
                              const isChildrenSelect = isOptionSelect(selectValue, childrenValue);
                              const childrenItemClasses = classNames('dropdown-options-item', {
                                'option-active': childrenValue === active,
                                'option-select': isChildrenSelect,
                              });
                              const formatChildrenLabel = markSearchValue(childrenLabel, searchValue, searchDiscriminateCase);
                              const formatChildrenSubLabel = markSearchValue(childrenSubLabel, searchValue, searchDiscriminateCase);
                              return (
                                <div
                                  key={childrenValue}
                                  style={setOptionStyle(columnNumber, itemIndex)}
                                  className={childrenItemClasses}
                                  onClick={() => handleClickOption(childrenValue)}
                                  onMouseEnter={() => handleItemMouseEnter(childrenValue)}
                                >
                                  {
                                    childrenIcon && (
                                      <span className='dropdown-options-item-icon'>{childrenIcon}</span>
                                    )
                                  }
                                  {
                                    typeof formatChildrenLabel === 'string' ? (
                                      <span className='dropdown-options-item-label' dangerouslySetInnerHTML={{__html: formatChildrenLabel}}></span>
                                    ) : (
                                      <span className='dropdown-options-item-label'>{formatChildrenLabel}</span>
                                    )
                                  }
                                  {
                                    typeof formatChildrenSubLabel === 'string' ? (
                                      <span className='dropdown-options-item-sub-label' dangerouslySetInnerHTML={{__html: formatChildrenSubLabel}}></span>
                                    ) : (
                                      <span className='dropdown-options-item-sub-label'>{formatChildrenSubLabel}</span>
                                    )
                                  }
                                  {
                                    isChildrenSelect && (
                                      <span className='dropdown-options-item-selected'>
                                        <CheckOutlined className='dropdown-options-item-selected-icon' />
                                      </span>
                                    )
                                  }
                                </div>
                              );
                            })
                          }
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div
                      key={value}
                      style={setOptionStyle(columnNumber, optionIndex)}
                      className={itemClasses}
                      onClick={() => handleClickOption(value)}
                      onMouseEnter={() => handleItemMouseEnter(value)}
                    >
                      {
                        !!icon && (
                          <span className='dropdown-options-item-icon'>{icon}</span>
                        )
                      }
                      {
                        typeof formatLabel === 'string' ? (
                          <span className='dropdown-options-item-label' dangerouslySetInnerHTML={{__html: formatLabel}}></span>
                        ) : (
                          <span className='dropdown-options-item-label'>{formatLabel}</span>
                        )
                      }
                      {
                        typeof formatSubLabel === 'string' ? (
                          <span className='dropdown-options-item-sub-label' dangerouslySetInnerHTML={{__html: formatSubLabel}}></span>
                        ) : (
                          <span className='dropdown-options-item-sub-label'>{formatLabel}</span>
                        )
                      }
                      {
                        isSelect && (
                          <span className='dropdown-options-item-selected'>
                            <CheckOutlined className='dropdown-options-item-selected-icon' />
                          </span>
                        )
                      }
                    </div>
                  );
                })
              }
            </>
          ) : (
            <div className="dropdown-options-empty">
              {
                selectOption?.length ? searchOptionsEmptyTip : optionsEmptyTip }
            </div>
          )
        }
      </div>
    </div>
  );
  return ReactDOM.createPortal(renderHTML, containerDom);
};

SelectTabDropdown.defaultProps = {
  columnNumber: 2,
  searchPlaceholder: '搜索内容',
  isKeyboardEvent: false,
  optionsEmptyTip: '暂无数据',
  searchOptionsEmptyTip: '没有与搜索内容匹配的选项',
};

export default SelectTabDropdown;
